import React from 'react'

import { ID, ServiceBase } from '~/service'
import { useDelay } from '~/util'

import './PureChat.scss'

/**
 * Render the PureChat script into the body.
 */
function buildScriptElement(id: ID) {
  const s = document.createElement('script')
  s.type = 'text/javascript'
  s.async = true
  s.dataset.cfasync = 'false'
  // s['data-cfasync'] = false
  s.innerHTML = `
    window.purechatApi = {
      l: [],
      t: [],
      on: function () {
        this.l.push(arguments);
      }
    };

    (function () {
      var done = false;
      var script = document.createElement('script');
      script.setAttribute('data-testid', 'purechat-script');
      script.async = true;
      script.type = 'text/javascript';
      script.src = 'https://app.purechat.com/VisitorWidget/WidgetScript';
      document.getElementsByTagName('HEAD').item(0).appendChild(script);
      script.onreadystatechange = script.onload = function (e) {
        if (!done && (!this.readyState || this.readyState == 'loaded' || this.readyState == 'complete')) {
          var w = new PCWidget({c: '${id}', f: true });
          done = true;
        }
      };
    })();
  `
  return s
}

/**
 * Override !important styles defined in PureChat css.
 *
 * The styles are added this way in order to have our style tag added after any tags created PureChat.
 * This is the only way for us to override any of their css which is all marked with !important.
 * Also, these replicate the specificity of their selectors in order to be able to do a css override.
 */
function buildStyleElement() {
  const style = document.createElement('style')
  style.type = 'text/css'
  style.setAttribute('data-testid', 'purechat-styles')
  style.innerHTML = `
    .purechat a, .purechat abbr, .purechat address, .purechat area, .purechat article, .purechat aside, .purechat audio, .purechat b, .purechat base, .purechat bdi, .purechat bdo, .purechat blockquote, .purechat body, .purechat br, .purechat button, .purechat canvas, .purechat caption, .purechat cite, .purechat code, .purechat col, .purechat colgroup, .purechat command, .purechat datalist, .purechat dd, .purechat del, .purechat details, .purechat dfn, .purechat div, .purechat dl, .purechat dt, .purechat em, .purechat embed, .purechat fieldset, .purechat figcaption, .purechat figure, .purechat footer, .purechat form, .purechat h1, .purechat h2, .purechat h3, .purechat h4, .purechat h5, .purechat h6, .purechat head, .purechat header, .purechat hgroup, .purechat hr, .purechat i, .purechat iframe, .purechat img, .purechat input, .purechat ins, .purechat kbd, .purechat keygen, .purechat label, .purechat legend, .purechat li, .purechat link, .purechat map, .purechat mark, .purechat menu, .purechat meta, .purechat meter, .purechat nav, .purechat noscript, .purechat object, .purechat ol, .purechat optgroup, .purechat option, .purechat output, .purechat p, .purechat param, .purechat pre, .purechat progress, .purechat q, .purechat rp, .purechat rt, .purechat ruby, .purechat s, .purechat samp, .purechat section, .purechat select, .purechat small, .purechat source, .purechat span, .purechat strong, .purechat style, .purechat sub, .purechat summary, .purechat sup, .purechat table, .purechat tbody, .purechat td, .purechat textarea, .purechat tfoot, .purechat th, .purechat thead, .purechat time, .purechat title, .purechat tr, .purechat track, .purechat u, .purechat ul, .purechat var, .purechat video, .purechat wbr, .purechat a:before, .purechat abbr:before, .purechat address:before, .purechat area:before, .purechat article:before, .purechat aside:before, .purechat audio:before, .purechat b:before, .purechat base:before, .purechat bdi:before, .purechat bdo:before, .purechat blockquote:before, .purechat body:before, .purechat br:before, .purechat button:before, .purechat canvas:before, .purechat caption:before, .purechat cite:before, .purechat code:before, .purechat col:before, .purechat colgroup:before, .purechat command:before, .purechat datalist:before, .purechat dd:before, .purechat del:before, .purechat details:before, .purechat dfn:before, .purechat div:before, .purechat dl:before, .purechat dt:before, .purechat em:before, .purechat embed:before, .purechat fieldset:before, .purechat figcaption:before, .purechat figure:before, .purechat footer:before, .purechat form:before, .purechat h1:before, .purechat h2:before, .purechat h3:before, .purechat h4:before, .purechat h5:before, .purechat h6:before, .purechat head:before, .purechat header:before, .purechat hgroup:before, .purechat hr:before, .purechat i:before, .purechat iframe:before, .purechat img:before, .purechat input:before, .purechat ins:before, .purechat kbd:before, .purechat keygen:before, .purechat label:before, .purechat legend:before, .purechat li:before, .purechat link:before, .purechat map:before, .purechat mark:before, .purechat menu:before, .purechat meta:before, .purechat meter:before, .purechat nav:before, .purechat noscript:before, .purechat object:before, .purechat ol:before, .purechat optgroup:before, .purechat option:before, .purechat output:before, .purechat p:before, .purechat param:before, .purechat pre:before, .purechat progress:before, .purechat q:before, .purechat rp:before, .purechat rt:before, .purechat ruby:before, .purechat s:before, .purechat samp:before, .purechat section:before, .purechat select:before, .purechat small:before, .purechat source:before, .purechat span:before, .purechat strong:before, .purechat style:before, .purechat sub:before, .purechat summary:before, .purechat sup:before, .purechat table:before, .purechat tbody:before, .purechat td:before, .purechat textarea:before, .purechat tfoot:before, .purechat th:before, .purechat thead:before, .purechat time:before, .purechat title:before, .purechat tr:before, .purechat track:before, .purechat u:before, .purechat ul:before, .purechat var:before, .purechat video:before, .purechat wbr:before, .purechat a:after, .purechat abbr:after, .purechat address:after, .purechat area:after, .purechat article:after, .purechat aside:after, .purechat audio:after, .purechat b:after, .purechat base:after, .purechat bdi:after, .purechat bdo:after, .purechat blockquote:after, .purechat body:after, .purechat br:after, .purechat button:after, .purechat canvas:after, .purechat caption:after, .purechat cite:after, .purechat code:after, .purechat col:after, .purechat colgroup:after, .purechat command:after, .purechat datalist:after, .purechat dd:after, .purechat del:after, .purechat details:after, .purechat dfn:after, .purechat div:after, .purechat dl:after, .purechat dt:after, .purechat em:after, .purechat embed:after, .purechat fieldset:after, .purechat figcaption:after, .purechat figure:after, .purechat footer:after, .purechat form:after, .purechat h1:after, .purechat h2:after, .purechat h3:after, .purechat h4:after, .purechat h5:after, .purechat h6:after, .purechat head:after, .purechat header:after, .purechat hgroup:after, .purechat hr:after, .purechat i:after, .purechat iframe:after, .purechat img:after, .purechat input:after, .purechat ins:after, .purechat kbd:after, .purechat keygen:after, .purechat label:after, .purechat legend:after, .purechat li:after, .purechat link:after, .purechat map:after, .purechat mark:after, .purechat menu:after, .purechat meta:after, .purechat meter:after, .purechat nav:after, .purechat noscript:after, .purechat object:after, .purechat ol:after, .purechat optgroup:after, .purechat option:after, .purechat output:after, .purechat p:after, .purechat param:after, .purechat pre:after, .purechat progress:after, .purechat q:after, .purechat rp:after, .purechat rt:after, .purechat ruby:after, .purechat s:after, .purechat samp:after, .purechat section:after, .purechat select:after, .purechat small:after, .purechat source:after, .purechat span:after, .purechat strong:after, .purechat style:after, .purechat sub:after, .purechat summary:after, .purechat sup:after, .purechat table:after, .purechat tbody:after, .purechat td:after, .purechat textarea:after, .purechat tfoot:after, .purechat th:after, .purechat thead:after, .purechat time:after, .purechat title:after, .purechat tr:after, .purechat track:after, .purechat u:after, .purechat ul:after, .purechat var:after, .purechat video:after, .purechat wbr:after {
      font-family: "Open Sans", -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
    }
    #PureChatWidget.purechat.purechat-popped-out-widget.purechat-widget-expanded.purechat-state-chatting,
    #PureChatWidget.purechat.purechat-popped-out-widget.purechat-widget-expanded.purechat-state-inactive {
      height: 100% !important;
    }
    #PureChatWidget.purechat.purechat-popped-out-widget {
      max-height: 100% !important;
    }
    #PureChatWidget.purechat .purechat-btn {
      border-radius: 20px !important;
    }
    #PureChatWidget.purechat .purechat-collapsed-image,
    #PureChatWidget.purechat .purechat-collapsed .purechat-widget-header,
    #PureChatWidget.purechat .purechat-widget-header .purechat-widget-room-avatar.purechat-has-room-avatar,
    #PureChatWidget.purechat.purechat-popped-out-widget.purechat-has-image .purechat-collapsed-image,
    #PureChatWidget.purechat.purechat-popped-out-widget.purechat-image-only.purechat-has-image.purechat-widget-collapsed .purechat-collapsed-image {
      display: none !important;
    }
  `
  return style
}

/**
 * Get the PureChat client API.
 */
function getPureChatAPI() {
  // @ts-ignore Property 'purechatApi' does not exist on type 'Window'
  return window.purechatApi
}

/**
 * Service for interacting with PureChat.
 */
export class PureChatAPI extends ServiceBase {
  clientId: ID
  element: HTMLElement | null = null

  constructor(
    clientId: ID,
    /**
     * A function that returns the current client instance on the window. This is
     * a function (rather than the client object) because the PureChat api client
     * changes at various points in its lifecycle.
     */
    client = getPureChatAPI
  ) {
    super(client)
    this.clientId = clientId
  }

  /**
   * @param {HTMLElement} element
   * @param {function} onClose
   * @param {function} [onReady]
   */
  init(element: HTMLElement, onClose: () => void, onReady: () => void) {
    if (this.clientId) {
      this.element = element

      // If the client doesn't exist yet, create it.
      if (!this.client()) {
        this.element.appendChild(buildScriptElement(this.clientId))
      }

      // If the client still doesn't exist, abort.
      if (!this.client()) {
        console.warn('Unable to initialize the PureChat client.')
        return
      }

      const handleReady = () => {
        this.element?.appendChild(buildStyleElement())
        if (onReady) onReady()
      }

      const handleClose = () => {
        // Not sure why a different instance is returned at
        // this point, but this updated client is the one we
        // need to use.
        if (this.client()?.get) {
          const expanded = this.client().get('chatbox.expanded')
          if (!expanded) onClose()
        }
      }

      // PureChat API Docs:
      // See https://purechat.com/support/javascript-api#region=chatbox-ready
      this.client().on('chatbox:ready', handleReady)
      this.client().on('chatbox:collapse', handleClose)
      this.client().on('chatbox:poppedOut', handleClose)
      this.client().on('chatbox:minimize', handleClose)
    }
  }

  setOpenState(isOpen: boolean) {
    // If we were able to get the chat client,
    // set the new open state.
    if (this.client()?.set) this.client().set('chatbox.expanded', isOpen)
  }
}

interface PureChatProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The id of the pure chat client for this community.
   */
  clientId: ID
  /**
   * Whether the chat should be visible.
   */
  isOpen: boolean
  /**
   * A callback that will be called when the
   * PureChat UI is used to close the chat widget.
   * You should update the `isOpen` state when this
   * is called in order to ensure the state stays
   * consistent with the PureChat UI.
   */
  onClose: (...args: unknown[]) => unknown
  /**
   * A callback that will be called once the
   * PureChat API has be initialized.
   */
  onReady?: (...args: unknown[]) => unknown
  client?: PureChatAPI
}

export function PureChatUI({
  clientId,
  isOpen,
  onClose,
  onReady,
  className,
  client = new PureChatAPI(clientId),
  ...rest
}: PureChatProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [isReady, setIsReady] = React.useState(false)
  const [api] = React.useState(client)

  // Because the PureChat API does not provide a way to
  // remove event listeners (or it is broken as of this writing),
  // we need an indirect way to make the open state available in
  // the close callback.
  const openState = React.useRef(false)
  openState.current = isOpen

  const handleClose = React.useCallback(() => {
    if (openState.current) onClose()
  }, [onClose])

  // Initialize the PureChat client.
  React.useEffect(() => {
    if (clientId && !isReady) {
      const onReady = () => setIsReady(true)
      api.init(ref.current!, handleClose, onReady)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Emit the ready event.
  React.useEffect(() => {
    if (onReady && isReady) onReady()
  }, [isReady, onReady])

  // Handle open state changes.
  React.useEffect(() => {
    if (isReady) {
      api.setOpenState(isOpen)
    }
  }, [isReady, isOpen, api])

  // Render a div where the script tag will be placed.
  return <div data-testid="PureChat" {...rest} ref={ref} />
}

export interface PureChatWithDelayProps extends PureChatProps {
  /**
   * The delay in milliseconds before the chat is rendered.
   */
  delay?: number
}

export function PureChatWithDelay({ delay, ...rest }: PureChatWithDelayProps) {
  const render = useDelay(delay)
  if (!render) {
    return null
  } else {
    return <PureChatUI {...rest} />
  }
}
